import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { sanitizeDecimal, parseTRNumber } from "@/lib/validators";

const fmt2 = (n) => Number(n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function Accounts() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ account_type: "customer", party_name: "", type: "alacak", amount: "", currency: "TRY", note: "" });

  const load = async () => setRows((await api.get("/cari")).data);
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.party_name?.trim()) return toast.error("Taraf adı zorunlu");
    const amt = parseTRNumber(form.amount);
    if (amt <= 0) return toast.error("Tutar sıfırdan büyük geçerli bir sayı olmalı");
    try {
      await api.post("/cari", { ...form, amount: amt });
      toast.success("Kaydedildi");
      setOpen(false);
      setForm({ account_type: "customer", party_name: "", type: "alacak", amount: "", currency: "TRY", note: "" });
      load();
    } catch (e) { toast.error("Hata"); }
  };

  const totalBorc = rows.reduce((s,r) => s + r.borc, 0);
  const totalAlacak = rows.reduce((s,r) => s + r.alacak, 0);

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-caps">Defter-i Kebir</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 font-light">Cari Hesap</h1>
        </div>
        <button onClick={()=>setOpen(true)} className="btn-primary-gold flex items-center gap-2" data-testid="new-cari-btn"><Plus size={14} /> Yeni Hareket</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card-flat p-6"><div className="label-caps">Toplam Alacak</div><div className="font-mono text-2xl mt-3 tabular-nums text-emerald-400">{fmt2(totalAlacak)} ₺</div></div>
        <div className="card-flat p-6"><div className="label-caps">Toplam Borç</div><div className="font-mono text-2xl mt-3 tabular-nums text-red-400">{fmt2(totalBorc)} ₺</div></div>
        <div className="card-flat p-6"><div className="label-caps">Net Bakiye</div><div className="font-mono text-2xl mt-3 tabular-nums" style={{color:"var(--gold)"}}>{fmt2(totalAlacak - totalBorc)} ₺</div></div>
      </div>

      <div className="card-flat overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-neutral-500 label-caps border-b border-white/5">
              <th className="text-left px-6 py-4">Taraf</th>
              <th className="text-left px-6 py-4">Tür</th>
              <th className="text-right px-6 py-4">Alacak</th>
              <th className="text-right px-6 py-4">Borç</th>
              <th className="text-right px-6 py-4">Bakiye</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.party_name} className="border-b border-white/5" data-testid={`cari-${r.party_name}`}>
                <td className="px-6 py-4">{r.party_name}</td>
                <td className="px-6 py-4 label-caps">{r.account_type === "customer" ? "Müşteri" : "Tedarikçi"}</td>
                <td className="px-6 py-4 text-right font-mono tabular-nums text-emerald-400">{fmt2(r.alacak)}</td>
                <td className="px-6 py-4 text-right font-mono tabular-nums text-red-400">{fmt2(r.borc)}</td>
                <td className="px-6 py-4 text-right font-mono tabular-nums" style={{color:"var(--gold)"}}>{fmt2(r.bakiye)} ₺</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-neutral-500">Kayıt yok</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-md card-flat p-8" style={{background:"var(--surface)"}}>
            <div className="flex justify-between items-start mb-6">
              <div><div className="label-caps">Yeni</div><div className="font-display text-2xl mt-1">Cari Hareket</div></div>
              <button onClick={()=>setOpen(false)}><X size={18} /></button>
            </div>
            <div className="space-y-4">
              <input placeholder="Taraf adı" value={form.party_name} onChange={(e)=>setForm({...form, party_name: e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" data-testid="cari-party" />
              <div className="grid grid-cols-2 gap-3">
                <select value={form.account_type} onChange={(e)=>setForm({...form, account_type: e.target.value})} className="border border-white/10 px-3 py-2 text-sm" style={{background:"var(--surface-2)"}}>
                  <option value="customer">Müşteri</option>
                  <option value="supplier">Tedarikçi</option>
                </select>
                <select value={form.type} onChange={(e)=>setForm({...form, type: e.target.value})} className="border border-white/10 px-3 py-2 text-sm" style={{background:"var(--surface-2)"}} data-testid="cari-type">
                  <option value="alacak">Alacak (+)</option>
                  <option value="borc">Borç (−)</option>
                </select>
              </div>
              <input type="text" inputMode="decimal" placeholder="0,00" value={form.amount} onChange={(e)=>setForm({...form, amount: sanitizeDecimal(e.target.value, { maxDecimals: 2 })})} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm font-mono" data-testid="cari-amount" />
              <input placeholder="Açıklama" value={form.note} onChange={(e)=>setForm({...form, note: e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm" />
            </div>
            <button onClick={submit} className="btn-primary-gold w-full mt-6" data-testid="cari-submit">Kaydet</button>
          </div>
        </div>
      )}
    </div>
  );
}
